import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import Selection from "components/Input/Selection/Selection";
import { Stack, Typography } from "@mui/material";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps, rules } from "../../utils";
import { useTranslation } from "react-i18next";

const AmexMerchantDetailSection = ({ mccOptions, updateGsiState }) => {
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <Stack gap={2} sx={{ padding: "1rem" }}>
      <Typography variant="h3">
        {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.subtitle.merchantDetails`)}
      </Typography>
      <Stack sx={{ margin: "1rem 0" }}>
        <Stack gap={3}>
          <Controller
            name={`merchantName`}
            rules={rules({
              maxLength: 60,
              required: true,
              validationTags: [
                "noLeadingWhitespace",
                "noFullwidthCharacters",
                "noSpecialSpaces",
              ],
            })}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.merchantName`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                sxParams={{
                  textError: error,
                }}
                errorMessage={error?.message || ""}
              />
            )}
          />
          <Controller
            name={`businessName`}
            control={control}
            rules={rules({
              maxLength: 32,
              required: true,
              validationTags: [
                "noLeadingWhitespace",
                "noFullwidthCharacters",
                "noSpecialSpaces",
                "noChineseCharacters",
              ],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.businessName`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                sxParams={{
                  textError: error,
                }}
                errorMessage={error?.message || ""}
              />
            )}
          />

          <Controller
            name={`mccCode`}
            control={control}
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                {...CustomComponentCommonProps(
                  t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.mccCode`),
                  value,
                  "Selection",
                  true,
                  true,
                )}
                options={mccOptions ?? []}
                onchangeFn={(e) => {
                  updateGsiState("AMEX", "AMEX_BBMSL", "mcc", e.target.value);
                  onChange(e.target.value);
                }}
                errorMessage={error?.message || ""}
                sxParams={{
                  selectionError: error,
                }}
              />
            )}
          />

          <Controller
            name={`merchantRegNo`}
            control={control}
            rules={rules({ validationTags: ["validBusinessRegNo"] })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.merchantRegNo`,
                  ),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                errorMessage={error?.message || ""}
                sxParams={{
                  textError: error,
                }}
              />
            )}
          />

          <Controller
            name={`contactPhone`}
            control={control}
            rules={rules({
              required: true,
              validationTags: ["validPhoneNumber"],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.contactPhone`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                errorMessage={error?.message || ""}
                sxParams={{
                  textError: error,
                }}
              />
            )}
          />

          <Controller
            name={`email`}
            control={control}
            rules={rules({
              validationTags: [
                "validEmail",
                "noFullwidthCharacters",
                "noSpecialSpaces",
              ],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.email`),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                errorMessage={error?.message || ""}
                sxParams={{
                  textError: error,
                }}
              />
            )}
          />

          <Controller
            name={`merchantAddress`}
            control={control}
            rules={rules({
              maxLength: 200,
              required: true,
              validationTags: ["noFullwidthCharacters", "noSpecialSpaces"],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.merchantAddress`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                errorMessage={error?.message || ""}
                sxParams={{
                  textError: error,
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default React.memo(AmexMerchantDetailSection);
